import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { Suspense } from 'react';
import lazyWithRetry from './utils/lazyWithRetry';
import RouteFallback from './components/RouteFallback';
import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BadgeProvider } from './context/BadgeContext';
import { useDarkMode } from './hooks/useDarkMode';
import { usePush } from './hooks/usePush';
import { useNativePush } from './hooks/useNativePush';
import { useSplashWarmup } from './hooks/useSplashWarmup';
import { useOtaUpdate } from './hooks/useOtaUpdate';
import { useIdleLogout } from './hooks/useIdleLogout';
import { getPlatformSettings } from './utils/api';
import { ensureE2EKey } from './utils/e2e';
import { applyFontScale, getFontScale } from './utils/displayPrefs';
import OfflineBanner    from './components/OfflineBanner';
import UpdateBanner     from './components/UpdateBanner';
import ServerWakeBanner from './components/ServerWakeBanner';
import PullToRefresh    from './components/PullToRefresh';
import DialogHost       from './components/DialogHost';
import LoadErrorBanner  from './components/LoadErrorBanner';
import WhatsNew         from './components/WhatsNew';
import SubscriptionGate from './components/SubscriptionGate';
import './styles/global.css';
import './styles/homepage.css';

import Homepage         from './pages/Homepage';
import SignIn           from './pages/SignIn';

import NewsFeed         from './pages/NewsFeed';

import { isNativeApp, isDesktopApp, nativeHome } from './utils/platform';
import BiometricGate from './components/BiometricGate';

// ── Lazily-loaded routes ────────────────────────────────────────────────
// Heavy or rarely-visited pages (admin, JAMB, editor, staff, public one-offs)
// load on demand instead of shipping inside the initial bundle, so a student
// signing in no longer downloads the admin portal to see their feed.
const SignUp = lazyWithRetry(() => import('./pages/SignUp'));
const Onboarding = lazyWithRetry(() => import('./pages/Onboarding'));
const VerifyEmail = lazyWithRetry(() => import('./pages/VerifyEmail'));
const VerifyPhone = lazyWithRetry(() => import('./pages/VerifyPhone'));
const StudentDashboard = lazyWithRetry(() => import('./pages/StudentDashboard'));
const TutorDashboard = lazyWithRetry(() => import('./pages/TutorDashboard'));
const MyCourses = lazyWithRetry(() => import('./pages/MyCourses'));
const Assignments = lazyWithRetry(() => import('./pages/Assignments'));
const Assessments = lazyWithRetry(() => import('./pages/Assessments'));
const QAForum = lazyWithRetry(() => import('./pages/QAForum'));
const QuestionPool = lazyWithRetry(() => import('./pages/QuestionPool'));
const Flashcards = lazyWithRetry(() => import('./pages/Flashcards'));
const PeerCourses = lazyWithRetry(() => import('./pages/PeerCourses'));
const DirectMessages = lazyWithRetry(() => import('./pages/DirectMessages'));
const StudyGroups = lazyWithRetry(() => import('./pages/StudyGroups'));
const StudyPlanner = lazyWithRetry(() => import('./pages/StudyPlanner'));
const Notifications = lazyWithRetry(() => import('./pages/Notifications'));
const Profile = lazyWithRetry(() => import('./pages/Profile'));
const PhotoGallery = lazyWithRetry(() => import('./pages/PhotoGallery'));
const Timetable = lazyWithRetry(() => import('./pages/Timetable'));
const PastQuestions = lazyWithRetry(() => import('./pages/PastQuestions'));
const Announcements = lazyWithRetry(() => import('./pages/Announcements'));
const Subscription = lazyWithRetry(() => import('./pages/Subscription'));
const Settings = lazyWithRetry(() => import('./pages/Settings'));
const Account = lazyWithRetry(() => import('./pages/Account'));
const Membership = lazyWithRetry(() => import('./pages/Membership'));
const TutorProfile = lazyWithRetry(() => import('./pages/TutorProfile'));
const StudentProfile = lazyWithRetry(() => import('./pages/StudentProfile'));
const CGPATracker = lazyWithRetry(() => import('./pages/CGPATracker'));
const ProgressReports = lazyWithRetry(() => import('./pages/ProgressReports'));
const StudentResults = lazyWithRetry(() => import('./pages/StudentResults'));
const Leaderboard = lazyWithRetry(() => import('./pages/Leaderboard'));
const Saved = lazyWithRetry(() => import('./pages/Saved'));
const StudyBuddies = lazyWithRetry(() => import('./pages/StudyBuddies'));
const TutorCoursePage = lazyWithRetry(() => import('./pages/TutorCoursePage'));
const SearchPage = lazyWithRetry(() => import('./pages/SearchPage'));
const ForgotPassword = lazyWithRetry(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazyWithRetry(() => import('./pages/ForgotPassword').then(m => ({ default: m.ResetPassword })));
const LearnHub = lazyWithRetry(() => import('./pages/Hubs').then(m => ({ default: m.LearnHub })));
const ProgressHub = lazyWithRetry(() => import('./pages/Hubs').then(m => ({ default: m.ProgressHub })));
const StaffLanding = lazyWithRetry(() => import('./pages/StaffLanding'));
const SecretaryPortal = lazyWithRetry(() => import('./pages/SecretaryPortal'));
const Certificates = lazyWithRetry(() => import('./pages/Certificates'));
const AdminPortal = lazyWithRetry(() => import('./pages/AdminPortal'));
const ResultVerify = lazyWithRetry(() => import('./pages/ResultVerify'));
const FlashcardPublic = lazyWithRetry(() => import('./pages/FlashcardPublic'));
const QuestionLoader = lazyWithRetry(() => import('./pages/QuestionLoader'));
const LoaderConsole = lazyWithRetry(() => import('./pages/LoaderConsole'));
const TeamMemberPortfolio = lazyWithRetry(() => import('./pages/TeamMemberPortfolio'));
const PairDevice = lazyWithRetry(() => import('./pages/PairDevice'));
const StudentProfileAdmin = lazyWithRetry(() => import('./pages/StudentProfileAdmin'));
const TutorProfileAdmin = lazyWithRetry(() => import('./pages/TutorProfileAdmin'));
const CoursePageAdmin = lazyWithRetry(() => import('./pages/CoursePageAdmin'));
const NewsPage = lazyWithRetry(() => import('./pages/NewsPage'));
const NewsArticle = lazyWithRetry(() => import('./pages/NewsArticle'));
const NewsEditor = lazyWithRetry(() => import('./pages/NewsEditor'));
const WriteForUs = lazyWithRetry(() => import('./pages/WriteForUs'));
const TermsOfService = lazyWithRetry(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazyWithRetry(() => import('./pages/PrivacyPolicy'));
const JambLanding = lazyWithRetry(() => import('./pages/JambLanding'));
const JambDashboard = lazyWithRetry(() => import('./pages/JambDashboard'));
const JambQuiz = lazyWithRetry(() => import('./pages/JambQuiz'));
const PUTMEPractice = lazyWithRetry(() => import('./pages/PUTMEPractice'));
const JambCutoffs = lazyWithRetry(() => import('./pages/JambCutoffs'));
const JambCAs = lazyWithRetry(() => import('./pages/JambCAs'));
const JambResults = lazyWithRetry(() => import('./pages/JambResults'));
const JambFlashcards = lazyWithRetry(() => import('./pages/JambFlashcards'));
const JambAssignments = lazyWithRetry(() => import('./pages/JambAssignments'));
const JambSignIn = lazyWithRetry(() => import('./pages/JambSignIn'));
const JambSignUp = lazyWithRetry(() => import('./pages/JambSignUp'));
const JambForgotPassword = lazyWithRetry(() => import('./pages/JambForgotPassword'));
const JambExam = lazyWithRetry(() => import('./pages/JambExam'));
const JambProfile = lazyWithRetry(() => import('./pages/JambProfile'));
const JambSettings = lazyWithRetry(() => import('./pages/JambSettings'));
const JambSubjects = lazyWithRetry(() => import('./pages/JambSubjects'));
const JambFinder = lazyWithRetry(() => import('./pages/JambFinder'));
const JambAggregate = lazyWithRetry(() => import('./pages/JambAggregate'));

// Root route. On the web this is the marketing homepage. In the native app the
// homepage is skipped: launch goes to sign-in (or the News Feed if a session
// already exists).
function RootRoute() {
  const { currentUser } = useAuth();
  // The desktop app opens to pairing (no session) or the feed (paired).
  // Desktop sends you to normal sign-in, which already links to pairing for
  // anyone who prefers to scan from their phone. It used to open straight on
  // /pair, and that page offered no way back — so signing in with an email and
  // password on the desktop app was impossible.
  if (isDesktopApp()) return <Navigate to={currentUser ? '/feed' : '/signin'} replace />;
  if (isNativeApp()) {
    // nativeHome() sends the student app to /signin|/feed and the management
    // (staff) app to /staff|the staff member's portal.
    return <Navigate to={nativeHome(currentUser)} replace />;
  }
  return <Homepage />;
}

function PrivateRoute({ children, tutorOnly, studentOnly, writerAllowed, jambOnly, jambAllowed, secretaryAllowed, loaderOnly, loaderAllowed }) {
  const { currentUser, isStudent, isTutor, isAdmin, isWriter, isLoader, isJambStudent } = useAuth();
  if (!currentUser) return <Navigate to="/signin" replace />;
  // Question Loaders are single-purpose accounts. Keep them inside the loader
  // console (plus Account/Settings) and out of the student/tutor surfaces.
  if (isLoader) return (loaderOnly || loaderAllowed) ? children : <Navigate to="/loader-console" replace />;
  if (currentUser.role === 'secretary' && !secretaryAllowed) return <Navigate to="/secretary" replace />;
  if (currentUser.profileComplete === false && !isAdmin && !isWriter) return <Navigate to="/onboarding" replace />;
  if (isWriter && !writerAllowed) return <Navigate to="/news/write" replace />;
  if (isJambStudent && !jambOnly && !jambAllowed) return <Navigate to="/jamb/dashboard" replace />;
  if (jambOnly && !isJambStudent) return <Navigate to="/dashboard" replace />;
  if (tutorOnly   && !isTutor)   return <Navigate to="/dashboard" replace />;
  if (studentOnly && !isStudent) return <Navigate to="/dashboard" replace />;
  if (loaderOnly  && !isLoader)  return <Navigate to="/dashboard" replace />;
  return children;
}

function JambRoute({ children }) {
  const { currentUser, isJambStudent } = useAuth();
  if (!currentUser) return <Navigate to="/signin" replace />;
  if (currentUser.profileComplete === false) return <Navigate to="/onboarding" replace />;
  if (!isJambStudent) return <Navigate to="/dashboard" replace />;
  return children;
}

function AuthOnly({ children }) {
  const { currentUser } = useAuth();
  if (!currentUser) return <Navigate to="/signin" replace />;
  return children;
}

function OnboardingRoute({ children }) {
  const { currentUser, isAdmin, isJambStudent } = useAuth();
  if (!currentUser) return <Navigate to="/signin" replace />;
  if (isAdmin) return <Navigate to="/admin" replace />;
  if (currentUser.role === 'writer') return <Navigate to="/news/write" replace />;
  if (currentUser.profileComplete !== false) {
    return isJambStudent ? <Navigate to="/jamb/dashboard" replace /> : <Navigate to="/dashboard" replace />;
  }
  return children;
}

// The Digital ID Card is a paid perk. 200L/300L students are on the free
// peer-led track (they never subscribe), so they don't get an ID card.
function IdCardRoute({ children }) {
  const { currentUser, isStudent } = useAuth();
  if (isStudent && ['200L', '300L'].includes(currentUser?.level)) return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { currentUser, isAdmin, isJambStudent } = useAuth();
  if (currentUser) {
    if (isAdmin) return <Navigate to="/admin" replace />;
    if (currentUser.role === 'writer') return <Navigate to="/news/write" replace />;
    if (currentUser.role === 'secretary') return <Navigate to="/secretary" replace />;
    if (currentUser.role === 'loader') return <Navigate to="/loader-console" replace />;
    if (isJambStudent) return <Navigate to="/jamb/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function SecretaryRoute({ children }) {
  const { currentUser } = useAuth();
  if (!currentUser) return <Navigate to="/signin" replace />;
  if (!['secretary', 'admin'].includes(currentUser.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function WriterRoute({ children }) {
  const { currentUser } = useAuth();
  if (!currentUser) return <Navigate to="/signin" replace />;
  if (currentUser.role !== 'writer' && currentUser.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { currentUser, isAdmin } = useAuth();
  if (!currentUser) return <Navigate to="/signin" replace />;
  if (!isAdmin)     return <Navigate to="/dashboard" replace />;
  return children;
}

function FeedRoute({ children }) {
  const { currentUser, isJambStudent } = useAuth();
  const [state, setState] = useState('loading');
  useEffect(() => {
    getPlatformSettings()
      .then(s => setState(s?.newsFeedEnabled === true ? 'enabled' : 'disabled'))
      .catch(() => setState('disabled'));
  }, []);
  if (!currentUser) return <Navigate to="/signin" replace />;
  if (state === 'loading') return null;
  if (state === 'disabled') return <Navigate to={isJambStudent ? '/jamb/dashboard' : '/dashboard'} replace />;
  return children;
}

function AppInner() {
  const _loc = useLocation();
  const { currentUser } = useAuth();
  useDarkMode();
  useSplashWarmup();
  useOtaUpdate();
  // Apply the saved text-size (whole-UI zoom) as early as possible.
  useEffect(() => { applyFontScale(getFontScale()); }, []);
  useEffect(() => { if (currentUser?._id) ensureE2EKey(); }, [currentUser?._id]);
  usePush(currentUser);
  useNativePush(currentUser);
  useIdleLogout();

  return (
    <>
      <OfflineBanner />
      <UpdateBanner />
      <ServerWakeBanner />
      <LoadErrorBanner />
      {/* App-only gesture: mobile browsers have their own pull-to-refresh,
          and doubling it up made the website feel like the app. */}
      {isNativeApp() && <PullToRefresh />}
      <DialogHost />
      {/* Shown on both surfaces: OTA v1.5.0 put these features in the installed
          app too, so the tour is accurate everywhere. Kept out of onboarding /
          verification / pairing, where an interruption would get in the way. */}
      {currentUser && !['/onboarding', '/verify-email', '/verify-phone', '/pair'].some(p => _loc.pathname.startsWith(p)) && <WhatsNew />}
      <ErrorBoundary routeKey={_loc.pathname}>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/"                element={<RootRoute />} />
        <Route path="/verify"          element={<ResultVerify />} />
        <Route path="/verify/:tid"     element={<ResultVerify />} />
        <Route path="/flashcard/:id"   element={<FlashcardPublic />} />
        <Route path="/load/:token"     element={<QuestionLoader />} />
        <Route path="/team/:id"        element={<TeamMemberPortfolio />} />
        <Route path="/signin"          element={<PublicOnly><SignIn /></PublicOnly>} />
        <Route path="/staff"           element={<StaffLanding />} />
        <Route path="/pair"            element={<PairDevice />} />
        <Route path="/signup"          element={<PublicOnly><SignUp /></PublicOnly>} />
        <Route path="/verify-email"    element={<AuthOnly><VerifyEmail /></AuthOnly>} />
        <Route path="/verify-phone"    element={<AuthOnly><VerifyPhone /></AuthOnly>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password"  element={<ResetPassword />} />

        <Route path="/onboarding"      element={<OnboardingRoute><Onboarding /></OnboardingRoute>} />

        <Route path="/feed"            element={<FeedRoute><NewsFeed /></FeedRoute>} />
        <Route path="/my-courses"      element={<PrivateRoute><SubscriptionGate pageName="My Courses"><MyCourses /></SubscriptionGate></PrivateRoute>} />
        <Route path="/assignments"     element={<PrivateRoute><SubscriptionGate pageName="Assignments"><Assignments /></SubscriptionGate></PrivateRoute>} />
        <Route path="/assessments"     element={<PrivateRoute><SubscriptionGate pageName="Assessments"><Assessments /></SubscriptionGate></PrivateRoute>} />
        <Route path="/qa-forum"        element={<PrivateRoute><QAForum /></PrivateRoute>} />
        <Route path="/quiz"            element={<PrivateRoute><QuestionPool /></PrivateRoute>} />
        <Route path="/course-manager"  element={<PrivateRoute tutorOnly><QuestionPool /></PrivateRoute>} />
        <Route path="/question-pool"   element={<PrivateRoute><QuestionPool /></PrivateRoute>} />
        <Route path="/messages"        element={<PrivateRoute jambAllowed><DirectMessages /></PrivateRoute>} />
        <Route path="/groups"          element={<PrivateRoute jambAllowed><StudyGroups /></PrivateRoute>} />
        <Route path="/planner"         element={<PrivateRoute jambAllowed><StudyPlanner /></PrivateRoute>} />
        <Route path="/secretary"       element={<SecretaryRoute><SecretaryPortal /></SecretaryRoute>} />
        <Route path="/notifications"   element={<PrivateRoute jambAllowed><Notifications /></PrivateRoute>} />
        <Route path="/profile"         element={<PrivateRoute><Profile /></PrivateRoute>} />
        <Route path="/progress-reports" element={<PrivateRoute studentOnly><ProgressReports /></PrivateRoute>} />
        <Route path="/my-results"      element={<PrivateRoute studentOnly><StudentResults /></PrivateRoute>} />
        <Route path="/leaderboard"     element={<PrivateRoute studentOnly><Leaderboard /></PrivateRoute>} />
        <Route path="/saved"           element={<PrivateRoute jambAllowed><Saved /></PrivateRoute>} />
        <Route path="/study-buddies"   element={<PrivateRoute studentOnly><StudyBuddies /></PrivateRoute>} />
        <Route path="/learn"           element={<PrivateRoute jambAllowed><LearnHub /></PrivateRoute>} />
        <Route path="/progress"        element={<PrivateRoute studentOnly><ProgressHub /></PrivateRoute>} />
        <Route path="/photos"          element={<PrivateRoute><SubscriptionGate pageName="Photo Gallery"><PhotoGallery /></SubscriptionGate></PrivateRoute>} />
        <Route path="/settings"        element={<PrivateRoute writerAllowed secretaryAllowed loaderAllowed><Settings /></PrivateRoute>} />
        <Route path="/account"         element={<PrivateRoute writerAllowed secretaryAllowed loaderAllowed><Account /></PrivateRoute>} />
        <Route path="/timetable"       element={<PrivateRoute><SubscriptionGate pageName="Timetable"><Timetable /></SubscriptionGate></PrivateRoute>} />
        <Route path="/past-questions"  element={<PrivateRoute><PastQuestions /></PrivateRoute>} />
        <Route path="/announcements"   element={<PrivateRoute><SubscriptionGate pageName="Announcements"><Announcements /></SubscriptionGate></PrivateRoute>} />
        <Route path="/tutor/:id"       element={<PrivateRoute><TutorProfile /></PrivateRoute>} />
        <Route path="/student/:id"     element={<PrivateRoute><StudentProfile /></PrivateRoute>} />
        <Route path="/cgpa-tracker"    element={<PrivateRoute><CGPATracker /></PrivateRoute>} />
        <Route path="/search"          element={<PrivateRoute><SubscriptionGate pageName="Search"><SearchPage /></SubscriptionGate></PrivateRoute>} />

        <Route path="/dashboard"       element={<PrivateRoute studentOnly><StudentDashboard /></PrivateRoute>} />
        <Route path="/flashcards"      element={<PrivateRoute studentOnly><Flashcards /></PrivateRoute>} />
        <Route path="/peer-courses"    element={<PrivateRoute studentOnly><PeerCourses /></PrivateRoute>} />
        {/* ID cards are issued from the admin portal now (student page retired). */}
        <Route path="/my-id"           element={<Navigate to="/account" replace />} />
        <Route path="/certificates"    element={<PrivateRoute studentOnly><SubscriptionGate pageName="Certificates"><Certificates /></SubscriptionGate></PrivateRoute>} />
        <Route path="/subscription"    element={<PrivateRoute studentOnly><Subscription /></PrivateRoute>} />
        <Route path="/membership"      element={<PrivateRoute studentOnly><Membership /></PrivateRoute>} />

        <Route path="/tutor-dashboard" element={<PrivateRoute tutorOnly><TutorDashboard /></PrivateRoute>} />

        <Route path="/loader-console"  element={<PrivateRoute loaderOnly><LoaderConsole /></PrivateRoute>} />

        <Route path="/courses/:code"       element={<PrivateRoute tutorOnly><TutorCoursePage /></PrivateRoute>} />
        <Route path="/news"              element={<NewsPage />} />
        <Route path="/news/write-for-us" element={<WriteForUs />} />
        <Route path="/news/write"        element={<WriterRoute><NewsEditor /></WriterRoute>} />
        <Route path="/news/:slug"        element={<NewsArticle />} />

        {/* Legal */}
        <Route path="/terms"             element={<TermsOfService />} />
        <Route path="/privacy"           element={<PrivacyPolicy />} />

        {/* Public JAMB pages */}
        <Route path="/jamb"             element={<JambLanding />} />
        <Route path="/jamb/signin"          element={<PublicOnly><JambSignIn /></PublicOnly>} />
        <Route path="/jamb/signup"          element={<PublicOnly><JambSignUp /></PublicOnly>} />
        <Route path="/jamb/forgot-password" element={<JambForgotPassword />} />
        <Route path="/jamb/quiz"        element={<JambQuiz />} />
        <Route path="/jamb/cutoffs"     element={<JambCutoffs />} />
        <Route path="/jamb/flashcards"  element={<JambFlashcards />} />

        {/* Protected JAMB pages */}
        <Route path="/jamb/dashboard"   element={<JambRoute><JambDashboard /></JambRoute>} />
        <Route path="/jamb/putme"       element={<JambRoute><PUTMEPractice /></JambRoute>} />
        <Route path="/jamb/cas"         element={<JambRoute><JambCAs /></JambRoute>} />
        <Route path="/jamb/results"     element={<JambRoute><JambResults /></JambRoute>} />
        <Route path="/jamb/assignments" element={<JambRoute><JambAssignments /></JambRoute>} />
        <Route path="/jamb/exam"        element={<JambRoute><JambExam /></JambRoute>} />
        <Route path="/jamb/profile"     element={<JambRoute><JambProfile /></JambRoute>} />
        <Route path="/jamb/my-id"       element={<Navigate to="/jamb/profile" replace />} />
        <Route path="/jamb/settings"    element={<JambRoute><JambSettings /></JambRoute>} />
        <Route path="/jamb/subjects"    element={<JambSubjects />} />
        <Route path="/jamb/finder"      element={<JambFinder />} />
        <Route path="/jamb/aggregate"   element={<JambAggregate />} />

        <Route path="/admin/students/:id"  element={<AdminRoute><StudentProfileAdmin /></AdminRoute>} />
        <Route path="/admin/tutors/:id"    element={<AdminRoute><TutorProfileAdmin /></AdminRoute>} />
        <Route path="/admin/courses/:code" element={<AdminRoute><CoursePageAdmin /></AdminRoute>} />
        <Route path="/admin/*"         element={<AdminRoute><AdminPortal /></AdminRoute>} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BadgeProvider>
          <BiometricGate>
            <AppInner />
          </BiometricGate>
        </BadgeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
